import { useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { LegacyCaseDetail } from "../types/legacyCases";
import {
  legacyCasesService,
  LegacyCaseRequestError,
  type LegacyCaseConflictCode,
} from "../services/legacyCases.service";
import {
  buildLegacyReviewRequest,
  createLegacyReviewValues,
  focusLegacyFormError,
  legacyOptionalDate,
  validateLegacyImport,
  type LegacyReviewValues,
} from "../utils/legacyReviewForm";
import {
  useImportLegacyCase,
  useLegacyPatientCandidates,
  useSaveLegacyReview,
} from "./useLegacyCases";
import { legacyReviewCapacityErrors } from "../utils/legacyReviewCapacity";

export function useLegacyReviewEditor(
  detail: LegacyCaseDetail,
  checkImportCollision?: () => Promise<boolean>
) {
  const [initialDetail] = useState(detail);
  const initialValues = useMemo(
    () => createLegacyReviewValues(initialDetail),
    [initialDetail]
  );
  const [revision, setRevision] = useState(detail.reviewRevision);
  const [confirmationOpened, setConfirmationOpened] = useState(false);
  const [latest, setLatest] = useState<LegacyCaseDetail | undefined>();
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState<LegacyCaseConflictCode>();
  const [savedAt, setSavedAt] = useState(detail.latestReview?.reviewedAt);
  const [patientCandidatesRevision, setPatientCandidatesRevision] = useState(0);
  const [checkingCollision, setCheckingCollision] = useState(false);
  const importKey = useRef(crypto.randomUUID());
  const saving = useSaveLegacyReview(detail.id);
  const importing = useImportLegacyCase(detail.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<LegacyReviewValues>({
    initialValues,
    validateInputOnBlur: true,
    validate: {
      firstName: (value, values) =>
        values.patientAction === "create" && !value.trim()
          ? "Enter the patient's first name."
          : null,
      lastName: (value, values) =>
        values.patientAction === "create" && !value.trim()
          ? "Enter the patient's last name."
          : null,
      dateOfBirth: legacyOptionalDate,
      dateReceived: legacyOptionalDate,
      dateCollected: legacyOptionalDate,
      reportedDate: legacyOptionalDate,
      patientAction: (value) =>
        value ? null : "Choose an existing patient or create a patient.",
      patientId: (value, values) =>
        values.patientAction === "match" && !value
          ? "Select the matching patient."
          : null,
      physicianId: (value, values) =>
        values.physicianAction === "match" && !value
          ? "Select the referring physician."
          : null,
    },
  });
  const candidateRequest = useMemo(
    () => buildLegacyReviewRequest(form.values, revision),
    [form.values, revision]
  );
  const patientCandidates = useLegacyPatientCandidates(
    detail.id,
    candidateRequest
  );
  const busy = saving.isPending || importing.isPending || checkingCollision;

  const loadSaved = useCallback(
    (updated: LegacyCaseDetail) => {
      const values = createLegacyReviewValues(updated);
      form.setValues(values);
      form.setInitialValues(values);
      form.resetDirty(values);
      form.clearErrors();
      setRevision(updated.reviewRevision);
      setSavedAt(updated.latestReview?.reviewedAt);
      setError("");
      setErrorCode(undefined);
      importKey.current = crypto.randomUUID();
    },
    [form]
  );
  const handleRecover = useCallback(async () => {
    try {
      const updated = await legacyCasesService.get(detail.id);
      queryClient.setQueryData(["legacy-case", detail.id], updated);
      setLatest(updated);
    } catch (failure) {
      setError((failure as LegacyCaseRequestError).message);
    }
  }, [detail.id, queryClient]);
  const handleFailure = useCallback(
    async (failure: unknown) => {
      const code =
        failure instanceof LegacyCaseRequestError ? failure.code : undefined;
      setErrorCode(code);
      setError(
        failure instanceof LegacyCaseRequestError
          ? failure.message
          : "The request could not be completed. Your draft is still here."
      );
      if (code === "stale_review" || code === "already_imported") {
        setConfirmationOpened(false);
        await handleRecover();
      }
      if (
        code === "patient_match_required" ||
        code === "patient_selection_changed" ||
        code === "patient_candidates_review_required" ||
        code === "case_number_exists" ||
        code === "source_conflicts_unacknowledged" ||
        code === "extraction_review_required"
      )
        setConfirmationOpened(false);
      if (code === "patient_selection_changed") {
        form.setFieldValue("patientReviewedIdentity", undefined);
        form.setFieldError(
          "patientId",
          "Refresh and review the selected patient identity before importing."
        );
      }
      if (code === "patient_candidates_review_required")
        form.setFieldValue("patientReviewedCandidateIds", undefined);
      if (code === "source_conflicts_unacknowledged") {
        form.setFieldValue("conflictsAcknowledged", false);
        form.setFieldError(
          "conflictsAcknowledged",
          "Review and acknowledge the source differences."
        );
        focusLegacyFormError(form, { conflictsAcknowledged: true });
      }
      if (code === "extraction_review_required") await handleRecover();
      if (code === "case_number_exists" && checkImportCollision)
        await checkImportCollision();
      if (code === "idempotency_key_conflict")
        importKey.current = crypto.randomUUID();
    },
    [checkImportCollision, form, handleRecover]
  );
  const handlePatientRecovery = useCallback(async () => {
    const values = form.getValues();
    form.setFieldValue("patientAction", "match");
    form.setFieldValue("patientReviewedIdentity", undefined);
    try {
      const [updated, candidates] = await Promise.all([
        legacyCasesService.get(detail.id),
        legacyCasesService.patients(
          detail.id,
          values.medicalRecordNumber || values.lastName
        ),
      ]);
      queryClient.setQueryData(["legacy-case", detail.id], {
        ...updated,
        patientCandidates: [...updated.patientCandidates, ...candidates],
      });
      queryClient.removeQueries({ queryKey: ["legacy-patients", detail.id] });
      setPatientCandidatesRevision((value) => value + 1);
      form.setFieldError(
        "patientId",
        "Select the existing patient whose identity matches the reviewed report."
      );
      focusLegacyFormError(form, { patientId: true });
    } catch (failure) {
      await handleFailure(failure);
    }
  }, [detail.id, form, handleFailure, queryClient]);
  const handleCandidateRecovery = useCallback(async () => {
    form.setFieldValue("patientReviewedCandidateIds", undefined);
    const refreshed = await patientCandidates.refetch();
    if (refreshed.error) await handleFailure(refreshed.error);
  }, [form, handleFailure, patientCandidates]);
  const persist = useCallback(async () => {
    try {
      const updated = await saving.mutateAsync(
        buildLegacyReviewRequest(form.getValues(), revision)
      );
      loadSaved(updated);
      notifications.show({
        color: "green",
        title: "Draft saved",
        message: `Revision ${updated.reviewRevision} is ready to continue reviewing.`,
        autoClose: 4000,
      });
      return updated;
    } catch (failure) {
      await handleFailure(failure);
      return undefined;
    }
  }, [form, handleFailure, loadSaved, revision, saving]);
  const handleSave = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      if (busy) return;
      const values = form.getValues();
      const capacityErrors = legacyReviewCapacityErrors(values);
      if (Object.keys(capacityErrors).length) {
        form.setErrors(capacityErrors);
        focusLegacyFormError(form, capacityErrors);
        setError(
          "Review the oversized draft fields before saving. Complete original text is retained in source evidence."
        );
        return;
      }
      if (!values.patientAction) {
        form.setFieldError(
          "patientAction",
          "Choose an existing patient or create a patient."
        );
        focusLegacyFormError(form, { patientAction: true });
        return;
      }
      setError("");
      await persist();
    },
    [busy, form, persist]
  );
  const handleImport = useCallback(async () => {
    if (busy) return;
    if (checkImportCollision) {
      setCheckingCollision(true);
      try {
        if (!(await checkImportCollision())) {
          setErrorCode("case_number_exists");
          setError(
            "This case number already exists. Open the existing case; your archive draft is retained."
          );
          return;
        }
      } finally {
        setCheckingCollision(false);
      }
    }
    if (
      form.getValues().patientAction === "create" &&
      (!patientCandidates.isSuccess || patientCandidates.isFetching)
    ) {
      setError(
        "Wait for the existing patient suggestions, or refresh them, before importing. Your draft can still be saved."
      );
      return;
    }
    const fieldValidation = form.validate();
    const errors = {
      ...fieldValidation.errors,
      ...validateLegacyImport(form.getValues(), detail, patientCandidates.data),
      ...legacyReviewCapacityErrors(form.getValues()),
    };
    if (Object.keys(errors).length) {
      form.setErrors(errors);
      focusLegacyFormError(form, errors);
      setError("Complete the highlighted fields before importing.");
      return;
    }
    setError("");
    if ((!revision || form.isDirty()) && !(await persist())) return;
    setConfirmationOpened(true);
  }, [
    busy,
    checkImportCollision,
    detail,
    form,
    patientCandidates,
    persist,
    revision,
  ]);
  const handleConfirm = useCallback(async () => {
    if (busy) return;
    try {
      const result = await importing.mutateAsync({
        reviewRevision: revision,
        confirmed: true,
        idempotencyKey: importKey.current,
      });
      notifications.show({
        title: result.alreadyImported
          ? "Case already imported"
          : "Legacy case imported",
        message:
          "The original report and reviewed information are available in case details.",
        color: "green",
        autoClose: 4000,
      });
      navigate(`/cases/${result.caseId}`);
    } catch (failure) {
      await handleFailure(failure);
    }
  }, [busy, handleFailure, importing, navigate, revision]);
  const handleCloseConfirmation = useCallback(() => {
    if (!busy) setConfirmationOpened(false);
  }, [busy]);
  const handleCloseConflict = useCallback(() => setLatest(undefined), []);
  const handleLoadConflict = useCallback(() => {
    if (latest) loadSaved(latest);
    setLatest(undefined);
  }, [latest, loadSaved]);
  const handleKeepConflict = useCallback(() => {
    if (latest) setRevision(latest.reviewRevision);
    form.setDirty({ patientAction: true });
    setLatest(undefined);
    setError("");
    importKey.current = crypto.randomUUID();
  }, [form, latest]);
  return {
    form,
    revision,
    confirmationOpened,
    latest,
    error,
    errorCode,
    savedAt,
    patientCandidatesRevision,
    patientCandidates,
    busy,
    saving,
    importing,
    handleSave,
    handleImport,
    handleConfirm,
    handleCloseConfirmation,
    handleRecover,
    handlePatientRecovery,
    handleCandidateRecovery,
    handleCloseConflict,
    handleLoadConflict,
    handleKeepConflict,
  };
}
